import { restoreSession } from '@/hooks/useAuth';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@stores/authStore';

import { AbakoLogoSmall } from '@/components/shared/AbakoLogoSmall';
import { AbakoLogoLarge } from '@/components/shared/AbakoLogoLarge';
import { Button } from '@/components/ui/Button';
import { useDaoConfig } from '@/hooks/useDaoConfig';

/**
 * AuthLayout - Layout for authentication pages (login, register)
 *
 * Figma design (76:69099): Split screen layout with brand content on left, auth form on right.
 * Left panel features:
 *   - Dark background with decorative SVG flow-field pattern
 *   - Centered Abako logo (~350px, rotated ~10deg) with green glow
 *   - Glassmorphism diamond shape behind logo
 *   - Circle shape to the right of logo
 *   - Top-left: small logo + "Abako" text
 *   - Bottom-left: "Welcome to Abako" title (36px bold)
 */
export function AuthLayout() {
  const { daoName } = useDaoConfig();
  const status = useAuthStore((state) => state.status);
  const sessionError = useAuthStore((state) => state.sessionError);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const welcomeSeen = useAuthStore((state) => state.welcomeSeen);
  const [hasHydrated, setHasHydrated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!hasHydrated || status === 'loading' || status === 'error') {
      return;
    }

    if (!isAuthenticated && location.pathname !== '/welcome') {
      const alreadySeenWelcome =
        welcomeSeen ||
        window.localStorage.getItem('abako-welcome-seen') === 'true';
      if (!alreadySeenWelcome) {
        navigate('/welcome', { replace: true });
      }
    }

    if (isAuthenticated && location.pathname === '/welcome') {
      navigate('/dashboard', { replace: true });
    }
  }, [
    hasHydrated,
    status,
    isAuthenticated,
    welcomeSeen,
    location.pathname,
    navigate,
  ]);

  return (
    <div className="flex min-h-screen font-sans">
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {/* Left side - Brand/visual (hidden on mobile, visible lg+) */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[--base-surface-2] lg:flex lg:w-1/2">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[url('/images/abako-bg-pattern.svg')] bg-cover bg-center" />

        {/* Top-left: Logo + brand name */}
        <AbakoLogoSmall className="absolute z-10 p-8" />

        {/* Center: Logo composition */}
        <div className="relative z-10 flex flex-1 items-center justify-center">
          {/* Container logo composition */}
          <div className="relative h-[405px] w-[405px]">
            {/* Main Abako logo (rotated ~10deg) */}
            <div className="absolute left-1/2 top-1/2 flex h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rotate-[9.95deg] items-center justify-center drop-shadow-[0_0_40px_rgba(54,211,153,0.3)]">
              <AbakoLogoLarge />
            </div>
            {/* Diamond shape */}
            <div className="glassEffect absolute left-[36px] top-[172px] h-[108px] w-[108px] rotate-[30deg] rounded-[--radi-6]" />
            {/* Circle shape (glassmorphism, to the right) */}
            <div className="glassEffect absolute right-[10px] top-[110px] h-[94px] w-[94px] rounded-full" />
          </div>
        </div>

        {/* Bottom-left: Welcome text */}
        <div className="relative z-10 p-8 pb-12">
          <h1 className="font-bold text-[--font-size-4xl] text-[--text-dark-primary]">
            Welcome to {daoName}
          </h1>
        </div>
      </div>
      {/* Right side - Auth content */}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex min-w-0 flex-1 items-center justify-center overflow-y-auto bg-[--base-surface-1] px-4 pb-8 pt-28 sm:px-[--spacing-19] sm:pt-[--spacing-19] lg:pb-0"
      >
        <div className="w-full max-w-[540px] [&_.onboardingContent]:!w-full [&_.onboardingContent]:px-4 sm:[&_.onboardingContent]:px-[--spacing-18] [&_.onboardingContent_.sr-only]:!h-px [&_.onboardingContent_.sr-only]:!w-px [&_.onboardingContent_.sr-only]:!p-0 [&_.signupBtn]:max-w-[calc(100%_-_2rem)] [&_.signupBtn]:flex-wrap [&_.signupBtn]:justify-end">
          {status === 'loading' ? (
            <p
              role="status"
              className="text-center text-[--text-dark-secondary]"
            >
              Checking session…
            </p>
          ) : status === 'error' ? (
            <div className="space-y-[--spacing-8] text-center">
              <p role="alert">{sessionError}</p>
              <Button onClick={() => void restoreSession()}>
                Retry session check
              </Button>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
